import {
  AloftArea,
  AloftPrice,
  Improvements,
  PorchPatioDeck,
  PositiveInt,
  PropertyIdentification,
  ReportJSON,
  Site,
  Transaction,
  isDefined,
  isUndefined,
  syntaxFNMA,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { CompAdjustments } from './CompAdjustments.ts';
import { selectCompAdjustments } from './adjustmentSelectors.ts';
import { selectCompSequence } from './orderSelectors.ts';
import { selectImprovementsEntities } from './propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from './propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectSiteEntities } from './propertyDataSelectors/siteSelectors.ts';
import { selectTransactionEntities } from './propertyDataSelectors/transactionSelectors.ts';
import { selectOrderHasExternalDepreciation } from './selectOrderDepreciation.ts';
import { selectSubjectCostApproach } from './selectSubjectCostApproach.ts';

export const selectReportJSON = createSelector(
  [
    selectCompSequence,
    selectTransactionEntities,
    selectPropertyIdentificationEntities,
    selectImprovementsEntities,
    selectSiteEntities,
    selectSubjectCostApproach,
    selectCompAdjustments,
    selectOrderHasExternalDepreciation,
  ],
  (
    compSequence,
    transactions,
    identification,
    improvements,
    sites,
    subjectCostApproach,
    compAdjustments,
    hasExternalDepreciation,
  ): ReportJSON | undefined => {
    if (
      isUndefined(compSequence) ||
      isUndefined(transactions) ||
      isUndefined(identification) ||
      isUndefined(improvements) ||
      isUndefined(sites)
    ) {
      return undefined;
    }

    const comps = compSequence.map((compID) => [
      compID,
      buildReportJSONProperty({
        transaction: transactions[compID],
        identification: identification[compID],
        improvements: improvements[compID],
        site: sites[compID],
        compAdjustments: compAdjustments[compID],
      }),
    ]);

    const reportJSON: ReportJSON = {
      compSequence,
      comps: Object.fromEntries(comps),
      subject: {
        property: buildReportJSONProperty({
          identification: identification.subject,
          improvements: improvements.subject,
          site: sites.subject,
          transaction: transactions.subject,
        }),
        costApproach: subjectCostApproach,
      },
      meta: {
        otherFeature2Label: hasExternalDepreciation ? 'External Obsolescence' : undefined,
      },
    };

    return reportJSON;
  },
);

function buildReportJSONProperty({
  transaction,
  identification,
  improvements,
  site,
  compAdjustments,
}: {
  transaction?: Transaction;
  identification?: PropertyIdentification;
  improvements?: Improvements;
  site?: Site;
  compAdjustments?: CompAdjustments;
}): ReportJSON['comps'][string] {
  const age = syntaxFNMA.actualAgeSyntax.encode({ age: improvements?.age });

  const carStorage =
    improvements?.garage?.spaces || improvements?.carport?.spaces
      ? syntaxFNMA.garageCarportSyntax.encode({
          ...(improvements?.garage?.spaces
            ? {
                garageSpaces: syntaxFNMA.schParkingSpaceNumber.parse(improvements.garage.spaces),
              }
            : {}),
          ...(improvements?.carport?.spaces
            ? {
                carportSpaces: syntaxFNMA.schParkingSpaceNumber.parse(improvements.carport.spaces),
              }
            : {}),
        })
      : undefined;

  const condition = improvements?.uadCondition
    ? syntaxFNMA.conditionSyntax.encode({
        condition: `C${improvements?.uadCondition}`,
      })
    : undefined;

  const basementArea =
    improvements?.basement?.totalArea || improvements?.basement?.finishedArea
      ? syntaxFNMA.basementLine1Syntax.encode({
          total: improvements.basement.totalArea
            ? AloftArea.schema.parse(improvements.basement.totalArea)
            : undefined,
          finished: improvements.basement.finishedArea
            ? AloftArea.schema.parse(improvements.basement.finishedArea)
            : undefined,
        })
      : undefined;

  const roomsBelowGrade =
    improvements?.basement?.fullBaths || improvements?.basement?.halfBaths
      ? syntaxFNMA.basementLine2Syntax.encode({
          fullBathCount: improvements?.basement?.fullBaths,
          halfBathCount: improvements?.basement?.halfBaths,
        })
      : undefined;

  const gla = improvements?.gla
    ? syntaxFNMA.grossLivingAreaSyntax.encode(
        AloftArea.fromNumber(improvements.gla.unitAmount, improvements.gla.unit),
      )
    : undefined;

  const quality = improvements?.uadQuality
    ? syntaxFNMA.qualitySyntax.encode({
        quality: `Q${improvements.uadQuality}`,
      })
    : undefined;

  const saleAmount = transaction?.price
    ? syntaxFNMA.priceSyntax.encode(
        AloftPrice.fromUnitAmount(transaction.price.unitAmount, transaction.price.currency),
      )
    : undefined;

  const saleDate =
    transaction?.status && (transaction.saleDate || transaction.contractDate)
      ? syntaxFNMA.dateOfSaleSyntax.encode({
          listingStatus: transaction.status,
          closeMonth: transaction.saleDate,
          contractMonth: transaction.contractDate,
        })
      : undefined;

  const siteLine = isDefined(site?.area)
    ? syntaxFNMA.areaSyntax.encode(AloftArea.schema.parse(site.area))
    : undefined;

  const porchPatioDeckLine = [
    formatPorchPatioOrDeck(improvements?.porch, 'Prch'),
    formatPorchPatioOrDeck(improvements?.patio, 'Pt'),
    formatPorchPatioOrDeck(improvements?.deck, 'Dck'),
  ]
    .filter(isDefined)
    .join(', ');

  const featuresLine = [
    isDefined(improvements?.adu) && 'ADU',
    isDefined(improvements?.attic) && 'Attic',
    isDefined(improvements?.barn) && 'Brn',
    isDefined(improvements?.fireplace) && 'FP',
    isDefined(improvements?.pool) && 'Pool',
    isDefined(improvements?.shed) && 'Shd',
    isDefined(improvements?.workshop) && 'Shop',
  ]
    .filter(Boolean)
    .join(', ');

  const otherFeature1Adj =
    AloftPrice.addWithNulls(
      compAdjustments?.adu.adjustment,
      compAdjustments?.attic.adjustment,
      compAdjustments?.barn.adjustment,
      compAdjustments?.fireplace.adjustment,
      compAdjustments?.manual.adjustment,
      // Note: this is the adjustment for both pool and spa
      compAdjustments?.pool.adjustment,
      compAdjustments?.shed.adjustment,
      compAdjustments?.workshop.adjustment,
      // Note: product said to lump foundation adj in here
      compAdjustments?.mainDwelling.foundation?.adjustment,
    ) ?? undefined;

  const numberOfUnits =
    improvements?.propertyType === 'multiFamily' ? improvements.numberOfUnits : undefined;

  return {
    salesGridData: {
      encoding: 'UAD_2022',
      annotations: {
        Age: age,
        BasementArea: basementArea,
        BasementFinish: roomsBelowGrade,
        CarStorage: carStorage,
        Condition: condition,
        DataSourceDescription: `${transaction?.mls}#${transaction?.mlsListingID}`,
        DateOfSale: saleDate,
        FinancingConcessions: transaction?.concessions,
        GrossLivingArea: gla,
        NumberOfUnits: numberOfUnits ? `${numberOfUnits} Units` : undefined,
        LatitudeNumber: `${identification?.coordinates?.latitude}`,
        Location: site?.location,
        LongitudeNumber: `${identification?.coordinates?.longitude}`,
        PropertyCity: identification?.address.CityName,
        PropertyPostalCode: identification?.address.PostalCode,
        PropertyRights: transaction?.termsOfSale,
        PropertySalesAmount: saleAmount,
        PropertyState: identification?.address.StateCode,
        PropertyStreetAddress: identification?.address.AddressLineText,
        Quality: quality,
        SaleType: transaction?.termsOfSale, // DEPRECATED - use SalesConcessions instead
        SalesConcessions: transaction?.termsOfSale,
        SiteArea: siteLine,
        TotalBathroomCount: formatBathrooms(improvements?.fullBaths, improvements?.halfBaths),
        View: site?.view,
        PorchDeck: porchPatioDeckLine,
        OtherFeature1: featuresLine,
      },
      adjustments: {
        // Calculated adjustments
        Age: undefined,
        BasementArea: compAdjustments?.basement.adjustment,
        BasementFinish: compAdjustments?.bathBelowGrade.adjustment,
        CarStorage: compAdjustments?.garageCarport.adjustment,
        Condition: compAdjustments?.condition.adjustment,
        DateOfSale: compAdjustments?.dateOfSale.adjustment,
        DesignStyle: undefined,
        EnergyEfficient: undefined,
        FinancingConcessions: compAdjustments?.concessions.adjustment,
        FunctionalUtility: compAdjustments?.functionalUtility.adjustment,
        GrossLivingArea: compAdjustments?.mainDwelling.gla.adjustment,
        NumberOfUnits: compAdjustments?.numberOfUnits?.adjustment,
        HeatingCooling: undefined,
        Location: undefined,
        PorchDeck: compAdjustments?.porchPatioDeck.adjustment,
        PropertyRights: undefined,
        Quality: compAdjustments?.quality.adjustment,
        RoomAboveGradeLine1: undefined,
        RoomAboveGradeLine2: compAdjustments?.bathAboveGrade.adjustment,
        SalesConcessions: compAdjustments?.termsOfSale.adjustment,
        SiteArea: compAdjustments?.site.size.adjustment,
        OtherFeature1: otherFeature1Adj?.equals(AloftPrice.zero) ? undefined : otherFeature1Adj,
        OtherFeature2: compAdjustments?.externalObsolescence.adjustment,
        OtherFeature3: undefined,
      },
    },
  };
}

function formatBathrooms(fullBaths?: PositiveInt, halfBaths?: PositiveInt): string | undefined {
  if (isUndefined(fullBaths) && isUndefined(halfBaths)) {
    return undefined;
  }

  const fullBathsStr = fullBaths?.toString() ?? 0;
  const halfBathsStr = halfBaths?.toString() ?? 0;

  return `${fullBathsStr}.${halfBathsStr}`;
}

function formatPorchPatioOrDeck(
  ftr: PorchPatioDeck | undefined,
  type: 'Prch' | 'Pt' | 'Dck',
): string | undefined {
  if (!ftr) return undefined;

  if (ftr.type === 'covered') return `Cvd${type}`;
  if (ftr.type === 'screen') return `Scr${type}`;
  return type;
}
